import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Sparkles,
    Clock,
    Calendar,
    ChevronRight,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Zap,
    ArrowRightLeft,
    Sun,
    Moon,
} from 'lucide-react';
import { useAppState } from '../../contexts/AppContext';

interface ScheduleConflict {
    id: string;
    type: 'overdue' | 'overlap' | 'overloaded' | 'suggestion';
    severity: 'high' | 'medium' | 'low';
    title: string;
    description: string;
    affectedItems: { type: 'task' | 'event'; id: string; title: string }[];
    suggestions: ScheduleSuggestion[];
}

interface ScheduleSuggestion {
    id: string;
    label: string;
    description: string;
    action: () => void;
}

export default function AppScheduleNegotiation() {
    const navigate = useNavigate();
    const { tasks, events, updateTask } = useAppState();
    const [resolvedConflicts, setResolvedConflicts] = useState<Set<string>>(new Set());
    const [showSuccess, setShowSuccess] = useState(false);

    // Analyze schedule for conflicts and suggestions
    const conflicts = useMemo(() => {
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        const conflicts: ScheduleConflict[] = [];

        // Check for overdue tasks
        const overdueTasks = tasks.filter(t =>
            !t.completed &&
            t.dueDate &&
            t.dueDate < todayStr &&
            !t.deletedAt
        );

        if (overdueTasks.length > 0) {
            conflicts.push({
                id: 'overdue-tasks',
                type: 'overdue',
                severity: 'high',
                title: `${overdueTasks.length} Overdue Task${overdueTasks.length > 1 ? 's' : ''}`,
                description: 'These tasks have passed their due date and need attention.',
                affectedItems: overdueTasks.map(t => ({ type: 'task', id: t.id, title: t.title })),
                suggestions: [
                    {
                        id: 'reschedule-today',
                        label: 'Reschedule all to today',
                        description: 'Move all overdue tasks to today',
                        action: () => {
                            overdueTasks.forEach(t => {
                                updateTask(t.id, { dueDate: todayStr });
                            });
                        },
                    },
                    {
                        id: 'reschedule-tomorrow',
                        label: 'Move to tomorrow',
                        description: 'Reschedule to tomorrow morning',
                        action: () => {
                            const tomorrow = new Date(today);
                            tomorrow.setDate(tomorrow.getDate() + 1);
                            const tomorrowStr = tomorrow.toISOString().split('T')[0];
                            overdueTasks.forEach(t => {
                                updateTask(t.id, { dueDate: tomorrowStr });
                            });
                        },
                    },
                ],
            });
        }

        // Check for overloaded day (too many high priority tasks)
        const todayHighPriority = tasks.filter(t =>
            !t.completed &&
            t.priority === 'high' &&
            (t.dueDate === todayStr || !t.dueDate) &&
            !t.deletedAt
        );

        if (todayHighPriority.length > 3) {
            const excessTasks = todayHighPriority.slice(3);
            conflicts.push({
                id: 'overloaded-day',
                type: 'overloaded',
                severity: 'medium',
                title: 'Schedule Overloaded',
                description: `You have ${todayHighPriority.length} high-priority tasks today. Consider spreading them out.`,
                affectedItems: excessTasks.map(t => ({ type: 'task', id: t.id, title: t.title })),
                suggestions: [
                    {
                        id: 'spread-week',
                        label: 'Spread across week',
                        description: 'Distribute excess tasks across the next few days',
                        action: () => {
                            excessTasks.forEach((t, i) => {
                                const newDate = new Date(today);
                                newDate.setDate(newDate.getDate() + i + 1);
                                updateTask(t.id, { dueDate: newDate.toISOString().split('T')[0] });
                            });
                        },
                    },
                    {
                        id: 'deprioritize',
                        label: 'Lower priority',
                        description: 'Change some tasks to medium priority',
                        action: () => {
                            excessTasks.forEach(t => {
                                updateTask(t.id, { priority: 'medium' });
                            });
                        },
                    },
                ],
            });
        }

        // Suggest focus time blocks
        const focusEvents = events.filter(e => e.type === 'focus' && !e.deletedAt);
        const incompleteTasks = tasks.filter(t => !t.completed && !t.deletedAt);

        if (focusEvents.length === 0 && incompleteTasks.length > 5) {
            conflicts.push({
                id: 'no-focus-time',
                type: 'suggestion',
                severity: 'low',
                title: 'No Focus Time Scheduled',
                description: 'You have many tasks but no dedicated focus blocks. Adding focus time can improve productivity.',
                affectedItems: [],
                suggestions: [
                    {
                        id: 'add-morning-focus',
                        label: 'Add morning focus block',
                        description: '9:00 AM - 11:00 AM focus time',
                        action: () => {
                            // This would add a focus event - for now just navigate to calendar
                            navigate('/app/calendar');
                        },
                    },
                    {
                        id: 'add-afternoon-focus',
                        label: 'Add afternoon focus block',
                        description: '2:00 PM - 4:00 PM focus time',
                        action: () => {
                            navigate('/app/calendar');
                        },
                    },
                ],
            });
        }

        // Check for tasks without due dates
        const noDueDateTasks = tasks.filter(t =>
            !t.completed &&
            !t.dueDate &&
            !t.deletedAt
        );

        if (noDueDateTasks.length > 3) {
            conflicts.push({
                id: 'unscheduled-tasks',
                type: 'suggestion',
                severity: 'low',
                title: `${noDueDateTasks.length} Unscheduled Tasks`,
                description: 'These tasks have no due date. Scheduling them can help with planning.',
                affectedItems: noDueDateTasks.slice(0, 5).map(t => ({ type: 'task', id: t.id, title: t.title })),
                suggestions: [
                    {
                        id: 'schedule-this-week',
                        label: 'Schedule this week',
                        description: 'Spread across the next 7 days',
                        action: () => {
                            noDueDateTasks.forEach((t, i) => {
                                const newDate = new Date(today);
                                newDate.setDate(newDate.getDate() + (i % 7));
                                updateTask(t.id, { dueDate: newDate.toISOString().split('T')[0] });
                            });
                        },
                    },
                ],
            });
        }

        return conflicts.filter(c => !resolvedConflicts.has(c.id));
    }, [tasks, events, resolvedConflicts, updateTask, navigate]);

    const handleResolve = (conflictId: string, suggestion: ScheduleSuggestion) => {
        suggestion.action();
        setResolvedConflicts(prev => new Set([...prev, conflictId]));
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
    };

    const handleDismiss = (conflictId: string) => {
        setResolvedConflicts(prev => new Set([...prev, conflictId]));
    };

    const getSeverityColor = (severity: ScheduleConflict['severity']) => {
        switch (severity) {
            case 'high': return '#EF4444';
            case 'medium': return '#F59E0B';
            case 'low': return '#0D5F6B';
        }
    };

    const getSeverityIcon = (severity: ScheduleConflict['severity']) => {
        switch (severity) {
            case 'high': return AlertTriangle;
            case 'medium': return Clock;
            case 'low': return Sparkles;
        }
    };

    return (
        <div className="min-h-screen bg-[#F5F8FA] pb-24">
            {/* Header */}
            <div className="bg-gradient-to-br from-[#0D5F6B] to-[#2A7A85] text-white px-4 py-6">
                <div className="flex items-center gap-3 mb-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 -ml-2 hover:bg-white/10 rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-xl font-semibold">Schedule Optimizer</h1>
                </div>
                <p className="text-white/80 text-sm">
                    AI-powered suggestions to optimize your schedule and resolve conflicts.
                </p>
            </div>

            {/* Success Toast */}
            {showSuccess && (
                <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-[#10B981] text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 z-50 animate-bounce">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Changes applied!</span>
                </div>
            )}

            <div className="p-4 space-y-4">
                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white rounded-xl p-3 text-center shadow-[0_2px_8px_rgba(13,95,107,0.06)]">
                        <Calendar className="w-5 h-5 mx-auto mb-1 text-[#0D5F6B]" />
                        <div className="text-lg font-semibold text-[#1A2832]">
                            {tasks.filter(t => !t.completed && !t.deletedAt).length}
                        </div>
                        <div className="text-xs text-[#7B8C9B]">Active Tasks</div>
                    </div>
                    <div className="bg-white rounded-xl p-3 text-center shadow-[0_2px_8px_rgba(13,95,107,0.06)]">
                        <Clock className="w-5 h-5 mx-auto mb-1 text-[#F59E0B]" />
                        <div className="text-lg font-semibold text-[#1A2832]">
                            {events.filter(e => !e.deletedAt).length}
                        </div>
                        <div className="text-xs text-[#7B8C9B]">Events</div>
                    </div>
                    <div className="bg-white rounded-xl p-3 text-center shadow-[0_2px_8px_rgba(13,95,107,0.06)]">
                        <AlertTriangle className="w-5 h-5 mx-auto mb-1 text-[#EF4444]" />
                        <div className="text-lg font-semibold text-[#1A2832]">
                            {conflicts.filter(c => c.severity === 'high').length}
                        </div>
                        <div className="text-xs text-[#7B8C9B]">Issues</div>
                    </div>
                </div>

                {/* All Clear Message */}
                {conflicts.length === 0 && (
                    <div className="bg-white rounded-2xl p-6 text-center shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                        <div className="w-16 h-16 mx-auto mb-4 bg-[#D9EEF2] rounded-full flex items-center justify-center">
                            <CheckCircle className="w-8 h-8 text-[#0D5F6B]" />
                        </div>
                        <h2 className="text-xl font-semibold text-[#1A2832] mb-2">All Clear!</h2>
                        <p className="text-[#7B8C9B]">
                            Your schedule looks good. No conflicts or issues detected.
                        </p>
                    </div>
                )}

                {/* Conflict Cards */}
                {conflicts.map(conflict => {
                    const SeverityIcon = getSeverityIcon(conflict.severity);
                    const severityColor = getSeverityColor(conflict.severity);

                    return (
                        <div
                            key={conflict.id}
                            className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_16px_rgba(13,95,107,0.08)]"
                        >
                            {/* Conflict Header */}
                            <div
                                className="px-4 py-3 flex items-center gap-3"
                                style={{ backgroundColor: `${severityColor}10` }}
                            >
                                <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center"
                                    style={{ backgroundColor: `${severityColor}20` }}
                                >
                                    <SeverityIcon className="w-4 h-4" style={{ color: severityColor }} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-[#1A2832]">{conflict.title}</h3>
                                    <p className="text-sm text-[#7B8C9B]">{conflict.description}</p>
                                </div>
                                <button
                                    onClick={() => handleDismiss(conflict.id)}
                                    className="p-1 hover:bg-white rounded-full transition-colors"
                                >
                                    <XCircle className="w-5 h-5 text-[#7B8C9B]" />
                                </button>
                            </div>

                            {/* Affected Items */}
                            {conflict.affectedItems.length > 0 && (
                                <div className="px-4 py-3 border-b border-[#DCE4EA]">
                                    <div className="text-xs font-medium text-[#7B8C9B] mb-2">AFFECTED ITEMS</div>
                                    <div className="space-y-1">
                                        {conflict.affectedItems.slice(0, 3).map(item => (
                                            <div
                                                key={item.id}
                                                className="flex items-center gap-2 text-sm text-[#4A5C6A]"
                                            >
                                                <ChevronRight className="w-3 h-3 text-[#7B8C9B]" />
                                                {item.title}
                                            </div>
                                        ))}
                                        {conflict.affectedItems.length > 3 && (
                                            <div className="text-xs text-[#7B8C9B]">
                                                +{conflict.affectedItems.length - 3} more
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Suggestions */}
                            <div className="p-4 space-y-2">
                                <div className="text-xs font-medium text-[#7B8C9B] mb-2">SUGGESTED ACTIONS</div>
                                {conflict.suggestions.map(suggestion => (
                                    <button
                                        key={suggestion.id}
                                        onClick={() => handleResolve(conflict.id, suggestion)}
                                        className="w-full flex items-center justify-between p-3 bg-[#F5F8FA] hover:bg-[#D9EEF2] rounded-xl transition-colors group"
                                    >
                                        <div className="text-left">
                                            <div className="font-medium text-[#1A2832] group-hover:text-[#0D5F6B]">
                                                {suggestion.label}
                                            </div>
                                            <div className="text-xs text-[#7B8C9B]">
                                                {suggestion.description}
                                            </div>
                                        </div>
                                        <Zap className="w-5 h-5 text-[#0D5F6B] opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })}

                {/* Pro Tips */}
                <div className="bg-gradient-to-br from-[#0D5F6B] to-[#2A7A85] rounded-2xl p-4 text-white">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-5 h-5" />
                        <h3 className="font-semibold">Pro Tips</h3>
                    </div>
                    <div className="space-y-2 text-sm text-white/90">
                        <div className="flex items-start gap-2">
                            <Sun className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>Schedule high-priority tasks in the morning when energy is highest</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <Moon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>Leave buffer time between meetings for unexpected delays</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <ArrowRightLeft className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>Use "liquid time" to compress tasks when you're in flow</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
